import { useEffect, useRef } from 'react';
import { ArrowDown, ArrowUpRight, Play, Coffee } from 'lucide-react';

// ── IMAGES ──────────────────────────────────────────────────────────
const HERO_IMG =
  'https://images.pexels.com/photos/18030336/pexels-photo-18030336.jpeg?auto=compress&cs=tinysrgb&w=1200';
const SECTION2_IMG =
  'https://images.pexels.com/photos/33841611/pexels-photo-33841611.jpeg?auto=compress&cs=tinysrgb&w=900';
const SECTION2_IMG_2 =
  'https://images.pexels.com/photos/18747624/pexels-photo-18747624.jpeg?auto=compress&cs=tinysrgb&w=900';

const SOUNDTRACK_IMG =
  'https://images.pexels.com/photos/29638995/pexels-photo-29638995.jpeg?auto=compress&cs=tinysrgb&w=1200';

const OCEAN_IMG =
  'https://images.pexels.com/photos/14118849/pexels-photo-14118849.jpeg?auto=compress&cs=tinysrgb&w=900';

const RULE_IMG =
  'https://images.pexels.com/photos/10053125/pexels-photo-10053125.jpeg?auto=compress&cs=tinysrgb&w=1200';

const BOND_LINK = 'https://thebondcoffee.com';
const PLAYLIST_LINK = 'https://open.spotify.com';

// ── FIVE SPOTS ───────────────────────────────────────────────────────
const SPOTS = [
  {
    num: '01',
    name: 'Castle Rock',
    copy: 'Sit on the rocks. Look east. Question your life choices in an extremely scenic location.',
    note: 'Best with: coffee + sweatshirt + absolutely nowhere to be.',
    micro: 'yes, the rocks are slippery',
    url: 'https://www.google.com/maps/search/?api=1&query=Castle+Rock+Marblehead+MA',
  },
  {
    num: '02',
    name: 'Chandler Hovey Park',
    copy: 'Drive all the way to the end of the Neck. Then keep going until there is basically nowhere else to go. Coffee. Lighthouse. Open ocean. Done.',
    note: 'Best on: windy mornings when staying in the car is completely acceptable.',
    micro: 'turn it up unless you are at Chandler Hovey at 6:30am',
    url: 'https://www.google.com/maps/search/?api=1&query=Chandler+Hovey+Park+Marblehead+MA',
  },
  {
    num: '03',
    name: 'Crocker Park',
    copy: 'Possibly the highest view-to-effort ratio in town. Walk approximately 45 seconds. Pretend you earned it. Marblehead Harbor is below you.',
    note: '',
    micro: 'you climbed almost nothing for this view',
    url: 'https://www.google.com/maps/search/?api=1&query=Crocker+Park+Marblehead+MA',
  },
  {
    num: '04',
    name: 'Fort Sewall',
    copy: 'Grab the wall overlooking the harbor. Watch boats leave. Decide briefly that you should own a boat. Remember what boats cost. Drink coffee instead.',
    note: '',
    micro: '',
    url: 'https://www.google.com/maps/search/?api=1&query=Fort+Sewall+Marblehead+MA',
  },
  {
    num: '05',
    name: 'Parker Lane / Parker Place',
    copy: 'A tiny public way ending at the harbor. No gift shop. No attraction. Basically just a bench and a ridiculously good view. Which is exactly the point.',
    note: 'Please respect neighbors, public access boundaries and posted parking rules.',
    micro: '',
    url: 'https://www.google.com/maps/search/?api=1&query=Parker+Lane+Marblehead+MA',
  },
];

// ── SOUNDTRACK ───────────────────────────────────────────────────────
// Every track below has been verified to exist on Spotify.
const TRACKS = [
  {
    num: '01',
    artist: 'Hayley Reardon',
    title: 'If I Had A Boat',
    tag: 'North Shore',
    url: 'https://open.spotify.com/track/3xc0nHFfX32oPFOTXmgLCq',
  note: 'Warm, reflective coastal folk. A Lyle Lovett cover that sounds like it belongs here.',
  },
  {
    num: '02',
    artist: 'Mason Daring & Jeanie Stahl',
    title: 'Marblehead Morning',
    tag: 'Marblehead',
    url: 'https://open.spotify.com/track/0dd6IssM25BLvXmg5udpS7',
    note: 'The heritage folk pick. Before "coastal folk" had an algorithm.',
  },
  {
    num: '03',
    artist: 'Sarah Blacker',
    title: 'Come What May',
    tag: 'Salem',
    url: 'https://open.spotify.com/track/2GHNjzvXU8xin9HbnEEp1R',
    note: 'Folk / Americana from a Salem songwriter. Warm acoustic morning energy.',
  },
  {
    num: '04',
    artist: 'Conor Ryan Hennessy',
    title: 'Crane Beach',
    tag: 'North Shore',
    url: 'https://open.spotify.com/track/2VTn1Yen7w3vQDIIPzRyp2',
    note: 'Rough-edged storytelling folk. North Shore regional character.',
  },
  {
    num: '05',
    artist: 'Hayley Reardon',
    title: 'Stay Soft',
    tag: 'North Shore',
    url: 'https://open.spotify.com/artist/4US8FhryQWWUOPmLmreZDW',
    note: 'Gentle closer. Fingerpicked and unhurried. Let it fade out.',
  },
];

// ── FIELD GUIDE ──────────────────────────────────────────────────────
const FIELD_GUIDE = [
  { label: 'Best place to do absolutely nothing', value: 'Castle Rock' },
  { label: 'Best view for the least effort', value: 'Crocker Park' },
  { label: 'Best place to remember you live on the ocean', value: 'Chandler Hovey' },
  { label: 'Best weather', value: "Whatever day you don't have plans" },
  { label: 'Best season', value: 'The two weeks between summer traffic and winter darkness' },
  { label: 'Best parking strategy', value: 'We have no useful advice' },
];

// ── OCEAN REPORT ─────────────────────────────────────────────────────
const OCEAN_REPORT = [
  { label: 'Water', value: 'Still there.' },
  { label: 'Surf', value: 'Probably less impressive than California.' },
  { label: 'Wind', value: 'Enough to ruin your hair.' },
  { label: 'Seagulls', value: 'Overconfident.' },
  { label: 'Chance you should go look at the ocean anyway', value: '100%' },
];

// ── BREW METHODS ─────────────────────────────────────────────────────
const BREW_METHODS = [
  { name: 'Drip', copy: '2 tablespoons coffee per 6 oz water' },
  { name: 'Pour Over', copy: '1:16 coffee-to-water ratio' },
  { name: 'French Press', copy: '4 minutes. Press. Drink.' },
  { name: 'Cold Brew', copy: 'Honestly, you probably already have your own system.' },
];

// ── BEAN ORIGINS ─────────────────────────────────────────────────────
const BEANS = [
  {
    country: 'Brazil',
    pct: '60%',
    role: 'The Foundation',
    farm: "Luis Otavio's farm in Canastra",
    copy: "From Luis Otavio's farm in Canastra comes the heart of our blend — rich, dependable, and deeply satisfying. Like the bedrock beneath Marblehead's harbor, this Brazilian coffee provides the chocolatey, nutty foundation that makes every morning feel like home.",
    notes: 'Bittersweet chocolate, candied peanuts, caramel, roasted almonds, high cocoa solids chocolate',
  },
  {
    country: 'Guatemala',
    pct: '40%',
    role: 'The Brightness',
    farm: 'Pulcal farm, Antigua',
    copy: "From the volcanic soils of Antigua's Pulcal farm comes the spark that brings our blend to life. Like the morning sun breaking over Marblehead Harbor, this Guatemalan coffee adds the brightness, sweetness, and complexity that turns a good cup into something extraordinary.",
    notes: 'Brown sugar sweetness, apple-like acidity, dark chocolate, almond, raisin, fig',
  },
];

// ── COMPONENT ────────────────────────────────────────────────────────
export default function LocalNoon() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    document.title = 'Devereux Dawn · Local Noon × Bond Coffee · Marblehead, MA';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Devereux Dawn — roasted exclusively for Local Noon by Bond Coffee. Coffee for people who know the ocean looks better before everyone else gets there. Marblehead, Massachusetts.');

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('amelia-in');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    document.querySelectorAll('.amelia-reveal').forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0f1f33] text-[#f4ece0] antialiased selection:bg-[#fda36b]/30">
      {/* ═══════════════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Moody rocky coastline under overcast skies"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0f1f33]/30 via-transparent to-[#0f1f33]/50" />
        <div className="absolute inset-x-0 bottom-0 z-[1] h-2/3 bg-gradient-to-t from-[#0f1f33] via-[#0f1f33]/65 to-transparent" />
        <div className="fog-layer absolute -left-1/4 top-1/4 z-[2] h-40 w-[150%] bg-gradient-to-r from-transparent via-white/6 to-transparent blur-2xl" />
        <div className="fog-layer absolute right-0 top-1/2 z-[2] h-32 w-[120%] bg-gradient-to-l from-transparent via-white/4 to-transparent blur-2xl" style={{ animationDelay: '4s' }} />

        <div className="relative z-10 mx-auto w-full max-w-xl px-6 pb-20 pt-32">
          <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#ffd0a3]">
            Roasted exclusively for Local Noon
          </p>
          <h1 className="font-serif text-6xl leading-[0.85] tracking-tight text-white sm:text-7xl">
            Devereux Dawn
          </h1>
          <p className="mt-7 max-w-sm text-lg leading-relaxed text-white/80">
            Coffee for people who know the ocean looks better before everyone else gets there.
          </p>
          <p className="mt-5 text-sm uppercase tracking-[0.2em] text-white/50">
            Roasted exclusively for Local Noon by Bond Coffee
            <br />
            Marblehead, Massachusetts
          </p>
          <button
            onClick={() => scrollTo('what-is')}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
          >
            Keep going <ArrowDown className="h-4 w-4" />
          </button>
          <p className="mt-8 font-serif text-base italic text-white/45">
            you're already here. might as well stay awhile.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2 — SO…WHAT IS LOCAL NOON?
          ═══════════════════════════════════════════════════════════════ */}
      <section id="what-is" className="scroll-mt-20 bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <h2 className="font-serif text-4xl leading-[0.95] text-white sm:text-5xl">
              Local coffee.<br />Local water.<br />Questionable weather.
            </h2>
          </div>

          <div className="amelia-reveal mt-10 space-y-5 text-[16px] leading-[1.7] text-white/72">
            <p>Local Noon is a shop on Washington Street that gets Marblehead right — beach, ski, and the in-between seasons nobody talks about. We thought they deserved their own coffee.</p>
            <p>So we roasted one. Exclusively. Just for them. We call it <span className="font-serif text-xl italic text-[#fda36b]">Devereux Dawn</span>.</p>
            <p>This is our little love letter to Marblehead. Not postcard Marblehead. The real one.</p>
            <p>
              Coffee in the car. Wet dogs. Cold ocean. Boat shoes worn completely unironically. Devereux before the parking lot fills up. And that weird moment in February when 43° feels legitimately warm.
            </p>
            <p>
              We roast this coffee right here on the North Shore using beans sourced from coffee-growing regions around the world.
            </p>
            <p>Because Marblehead grows many things.</p>
            <p className="font-serif text-xl italic text-[#fda36b]">Coffee is not one of them.</p>
          </div>

          <div className="amelia-reveal mt-10 grid grid-cols-2 gap-3">
            <div className="overflow-hidden rounded-xl">
              <img src={SECTION2_IMG} alt="Rocky Plymouth shoreline" className="h-44 w-full object-cover" loading="lazy" />
            </div>
            <div className="overflow-hidden rounded-xl">
              <img src={SECTION2_IMG_2} alt="Beach grass and dunes" className="h-44 w-full object-cover" loading="lazy" />
            </div>
          </div>
          <p className="amelia-reveal mt-3 text-xs italic text-white/35">
            approximately 3 minutes before someone complained about parking
          </p>

          <div className="amelia-reveal mt-12">
            <p className="font-serif text-2xl leading-tight text-white">Drink it here.</p>
            <p className="font-serif text-2xl leading-tight text-[#fda36b]">Take it somewhere good.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3 — THE LOCAL NOON 5
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#132947] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">The Local Noon 5</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              Five places to drink this coffee before everyone else wakes up.
            </h2>
          </div>

          <div className="mt-12 space-y-5">
            {SPOTS.map((spot) => (
              <article key={spot.num} className="amelia-reveal rounded-2xl border border-white/8 bg-white/[0.03] p-6">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-3xl leading-none text-[#fda36b]/30 tabular-nums">{spot.num}</span>
                  <h3 className="font-serif text-2xl text-white">{spot.name}</h3>
                </div>
                <p className="mt-4 text-[15px] leading-relaxed text-white/68">{spot.copy}</p>
                {spot.note && (
                  <p className="mt-4 text-sm italic leading-relaxed text-[#fda36b]/70">{spot.note}</p>
                )}
                {spot.micro && (
                  <p className="mt-3 text-xs italic text-white/30">{spot.micro}</p>
                )}
                <a
                  href={spot.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#fda36b] transition hover:text-white"
                >
                  Open map <ArrowUpRight className="h-4 w-4" />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4 — THE LOCAL NOON SOUNDTRACK
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-[#0f1f33]">
        <img
          src={SOUNDTRACK_IMG}
          alt="Misty harbor morning"
          className="absolute inset-0 z-0 h-full w-full object-cover opacity-10"
          loading="lazy"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0f1f33]/90 via-[#0f1f33]/92 to-[#0f1f33]" />
        <div className="fog-layer absolute -left-1/4 top-1/3 z-[2] h-32 w-[150%] bg-gradient-to-r from-transparent via-white/3 to-transparent blur-2xl" />

        <div className="relative z-10 mx-auto max-w-xl px-6 py-24">
          <div className="amelia-reveal text-center">
            <div className="mx-auto mb-5 flex items-center justify-center gap-2 text-[#fda36b]">
              <span className="h-px w-8 bg-[#fda36b]/30" />
              <Play className="h-4 w-4" />
              <span className="h-px w-8 bg-[#fda36b]/30" />
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">The Local Noon soundtrack</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              Songs for when summer's over but you're not ready to admit it.
            </h2>
            <p className="mt-5 max-w-md mx-auto text-[15px] leading-relaxed text-white/50">
              Five songs for coffee, cold water, open windows and taking the long way home.
            </p>
          </div>

          {/* Track cards — back-of-the-bag styling */}
          <div className="mt-12 overflow-hidden rounded-2xl border border-white/8 bg-[#132947]/40">
            {TRACKS.map((track, i) => (
              <a
                key={track.num}
                href={track.url}
                target="_blank"
                rel="noopener noreferrer"
                className="amelia-reveal group flex items-center gap-4 border-b border-white/5 px-6 py-5 transition last:border-b-0 hover:bg-[#fda36b]/5"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="font-serif text-3xl leading-none text-[#fda36b]/20 tabular-nums transition group-hover:text-[#fda36b]/40">
                  {track.num}
                </span>
                <div className="h-10 w-px bg-white/8" />
                <div className="flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">{track.artist}</p>
                  <p className="font-serif text-lg leading-tight text-white">{track.title}</p>
                  <p className="mt-1 text-[13px] italic leading-relaxed text-white/35">{track.note}</p>
                  <span className="mt-2 inline-block rounded-full border border-[#fda36b]/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#fda36b]/60">
                    {track.tag}
                  </span>
                </div>
                <div className="flex shrink-0 flex-col items-center gap-1 text-[#fda36b]/40 transition group-hover:text-[#fda36b]">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em]">Listen</span>
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </a>
            ))}
          </div>

          <div className="amelia-reveal mt-10 text-center">
            <a
              href={PLAYLIST_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
            >
              <Play className="h-4 w-4 fill-current" /> Play the whole thing
            </a>
            <p className="mt-4 font-serif text-sm italic text-white/35">
              roughly 19 minutes of pretending you don't have anywhere to be.
            </p>
            <p className="mt-2 text-xs italic text-white/25">
              turn it up unless you are at Chandler Hovey at 6:30am
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 5 — OCEAN REPORT
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#132947] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">Scientifically verified by absolutely nobody</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              Today's very scientific<br />Marblehead ocean report
            </h2>
          </div>

          <div className="amelia-reveal mt-10 overflow-hidden rounded-2xl border border-white/8 bg-white/[0.03]">
            {OCEAN_REPORT.map((item, i) => (
              <div
                key={item.label}
                className={`flex items-center justify-between gap-4 px-6 py-5 ${i !== OCEAN_REPORT.length - 1 ? 'border-b border-white/5' : ''}`}
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">{item.label}</span>
                <span className="text-right font-serif text-lg text-white">{item.value}</span>
              </div>
            ))}
          </div>

          <div className="amelia-reveal mt-8 overflow-hidden rounded-2xl">
            <img src={OCEAN_IMG} alt="Waves crashing on rocks" className="h-48 w-full object-cover" loading="lazy" />
          </div>

          <p className="amelia-reveal mt-6 text-center text-sm italic text-white/35">
            Local Noon recommends consulting an actual weather service before making maritime decisions.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 6 — HOW TO MAKE IT
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">How to make it</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              Don't overthink the coffee.
            </h2>
          </div>

          <div className="amelia-reveal mt-10 space-y-4">
            {BREW_METHODS.map((method) => (
              <div key={method.name} className="rounded-xl border border-white/8 bg-white/[0.03] p-6">
                <div className="flex items-center gap-3">
                  <Coffee className="h-5 w-5 text-[#fda36b]" />
                  <h3 className="font-serif text-xl text-white">{method.name}</h3>
                </div>
                <p className="mt-3 text-[15px] leading-relaxed text-white/68">{method.copy}</p>
              </div>
            ))}
          </div>

          <p className="amelia-reveal mt-8 text-center font-serif text-lg italic text-white/40">
            Coffee tastes best however you actually like drinking it.
          </p>
          <p className="amelia-reveal mt-2 text-center text-xs italic text-white/30">
            We are not calling the coffee police. Measuring spoons optional.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 7 — ABOUT THE COFFEE
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#132947] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">About the coffee</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              Roasted here.<br />Grown very much not here.
            </h2>
          </div>

          <div className="amelia-reveal mt-8 space-y-5 text-[16px] leading-[1.7] text-white/72">
            <p>
              Devereux Dawn was roasted exclusively for Local Noon. We built the blend from two origins — one for foundation, one for brightness — and roasted it right here on the North Shore.
            </p>
            <p>
              We roast in small batches and care deeply about making coffee that tastes really good without requiring you to use words like "mouthfeel."
            </p>
          </div>

          <div className="amelia-reveal mt-6 flex flex-wrap gap-3">
            <span className="rounded-full border border-[#fda36b]/20 bg-[#fda36b]/[0.04] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#fda36b]/70">Devereux Dawn</span>
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white/50">Sun-up Sips</span>
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-white/50">Beach Town Easy</span>
          </div>
          <p className="amelia-reveal mt-3 text-xs italic text-white/30">same coffee, three names, depending on who's asking.</p>

          {/* Bean origin cards */}
          <div className="amelia-reveal mt-10 space-y-4">
            {BEANS.map((bean) => (
              <div key={bean.country} className="rounded-2xl border border-white/8 bg-white/[0.03] p-6">
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#fda36b]">{bean.country} · {bean.pct}</p>
                    <h3 className="mt-1 font-serif text-2xl text-white">{bean.role}</h3>
                  </div>
                  <p className="text-right text-xs italic text-white/35">{bean.farm}</p>
                </div>
                <p className="mt-4 text-[15px] leading-relaxed text-white/68">{bean.copy}</p>
                <div className="mt-4 rounded-lg border border-[#fda36b]/15 bg-[#fda36b]/[0.04] px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#fda36b]/60">Tasting Notes</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/65">{bean.notes}</p>
                </div>
              </div>
            ))}
          </div>

          {/* World map illustration */}
          <div className="amelia-reveal mt-10 rounded-2xl border border-white/8 bg-[#0f1f33]/60 p-8">
            <div className="relative mx-auto" style={{ maxWidth: '320px' }}>
              <svg viewBox="0 0 320 160" className="w-full" fill="none">
                {/* Simplified continents */}
                <path d="M40 50 Q55 40 70 45 L80 55 Q75 65 65 68 L50 65 Z" fill="#fda36b" opacity="0.12" />
                <path d="M85 55 Q100 50 115 58 L120 75 Q110 85 95 82 L85 70 Z" fill="#fda36b" opacity="0.12" />
                <path d="M130 45 Q150 40 170 50 L175 70 Q160 80 140 75 L130 60 Z" fill="#fda36b" opacity="0.12" />
                <path d="M180 60 Q200 55 215 65 L220 80 Q205 88 190 85 L180 72 Z" fill="#fda36b" opacity="0.12" />
                <path d="M220 40 Q240 35 260 45 L265 60 Q250 68 235 65 L225 52 Z" fill="#fda36b" opacity="0.12" />
                {/* Marblehead dot */}
                <circle cx="78" cy="52" r="4" fill="#fda36b" />
                <circle cx="78" cy="52" r="8" fill="#fda36b" opacity="0.2" />
                {/* Brazil dot */}
                <circle cx="150" cy="72" r="4" fill="#fda36b" opacity="0.6" />
                {/* Guatemala dot */}
                <circle cx="100" cy="60" r="4" fill="#fda36b" opacity="0.6" />
                {/* Lines from origins to Marblehead */}
                <path d="M150 72 Q120 60 78 52" stroke="#fda36b" strokeWidth="0.5" opacity="0.35" strokeDasharray="2 2" />
                <path d="M100 60 Q88 55 78 52" stroke="#fda36b" strokeWidth="0.5" opacity="0.35" strokeDasharray="2 2" />
              </svg>
            </div>
            <p className="mt-6 text-center font-serif text-xl text-white">The beans travel. The roaster doesn't.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 8 — THE LOCAL NOON RULE
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        <img
          src={RULE_IMG}
          alt="Rocky beach with waves"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0f1f33]/85 via-[#0f1f33]/75 to-[#0f1f33]" />

        <div className="relative z-10 mx-auto max-w-xl px-6 py-32 text-center">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">The Local Noon rule</p>
            <h2 className="mt-8 font-serif text-4xl leading-[1.1] text-white sm:text-5xl">
              If you can see the ocean,
              <br />
              sit there five minutes longer.
            </h2>
            <p className="mt-8 font-serif text-xl italic text-[#fda36b]">That's it.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 9 — FIELD GUIDE
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#0f1f33] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">A little Marblehead field guide</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              While you're out…
            </h2>
          </div>

          <div className="amelia-reveal mt-10 space-y-3">
            {FIELD_GUIDE.map((item, i) => (
              <div
                key={i}
                className={`flex flex-col gap-1 rounded-xl border border-white/8 bg-white/[0.03] p-5 ${i % 2 === 1 ? 'ml-6' : ''}`}
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/40">{item.label}</p>
                <p className="font-serif text-lg text-white">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 10 — ABOUT BOND
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#132947] py-24">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#fda36b]">About Bond</p>
            <h2 className="mt-4 font-serif text-3xl leading-[0.95] text-white sm:text-4xl">
              Made by your neighbors.
            </h2>
          </div>

          <div className="amelia-reveal mt-8 space-y-5 text-[16px] leading-[1.7] text-white/72">
            <p>
              Bond Coffee started in Marblehead because two neighbors thought making coffee together sounded fun.
            </p>
            <p>It still does.</p>
            <p>
              We roast small batches, make weird little projects like this one, support local causes whenever we can and try not to take coffee — or ourselves — too seriously.
            </p>
          </div>

          <div className="amelia-reveal mt-10">
            <a
              href={BOND_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#fda36b] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#132947] transition hover:bg-[#fda36b]/90 hover:-translate-y-0.5"
            >
              See what Bond is making <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 11 — BOTTOM OF PAGE
          ═══════════════════════════════════════════════════════════════ */}
      <footer className="bg-[#0f1f33] py-24 text-center">
        <div className="mx-auto max-w-xl px-6">
          <div className="amelia-reveal">
            <p className="font-serif text-3xl leading-tight text-white sm:text-4xl">
              You have reached<br />the end of the internet.
            </p>
            <p className="mt-6 font-serif text-xl italic text-[#fda36b]">Go look at the ocean.</p>
          </div>

          <div className="amelia-reveal mt-16 border-t border-white/8 pt-10">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-white/60">
              Devereux Dawn <span className="text-white/30">·</span> Local Noon <span className="text-white/30">×</span> Bond Coffee
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.14em] text-white/35">
              Marblehead, Massachusetts
            </p>
            <p className="mt-8 text-xs italic text-white/25">Made near the water.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
